import { useState, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useOpenOnQueryParam } from '../hooks/useOpenOnQueryParam'
import {
  Plus, Phone, Mail, ChevronRight, UserPlus,
  Stethoscope, CalendarPlus, Download, Baby, LayoutList, Eye, EyeOff,
  Users, UserCheck, CalendarCheck, UserX, LayoutGrid, Table,
} from 'lucide-react'
import { DataTable } from '../components/ui/DataTable'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { ErrorState } from '../components/ui/ErrorState'
import { CreatePatientModal } from '../components/patientDetail/CreatePatientModal'
import { Patient, CreatePatientInput } from '../types/patient'
import api from '../lib/api'
import toast from 'react-hot-toast'

interface User {
  id: string
  firstName: string
  lastName: string
  role: string
}

const statusMap: Record<string, { label: string; variant: any }> = {
  ACTIVE: { label: 'Activo', variant: 'success' },
  INACTIVE: { label: 'Inactivo', variant: 'warning' },
  ARCHIVED: { label: 'Archivado', variant: 'default' },
}

function getAge(birthDate: string | null): number | null {
  if (!birthDate) return null
  const diff = Date.now() - new Date(birthDate).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25))
}

// WhatsApp oculto hasta tener WABA API
// function normalizePhoneForWhatsApp(phone: string | null): string {
//   if (!phone) return ''
//   const digits = phone.replace(/\D/g, '')
//   if (digits.startsWith('+') || digits.startsWith('00')) {
//     return digits.replace(/^00/, '')
//   }
//   return '34' + digits
// }

interface PatientsResponse {
  data: Patient[]
  total: number
  page: number
  totalPages: number
}

export function PatientsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read filters from URL
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'ALL')
  const [alertsFilter, setAlertsFilter] = useState(searchParams.get('alerts') || 'ALL')
  const [dentistFilter, setDentistFilter] = useState(searchParams.get('dentistId') || 'ALL')
  const [ageFilter, setAgeFilter] = useState(searchParams.get('age') || 'ALL')
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10))
  const [showCreate, setShowCreate] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [showColumnMenu, setShowColumnMenu] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('patients-visible-columns')
      return saved ? JSON.parse(saved) : ['code', 'dentist', 'status', 'nextApt', 'alerts']
    } catch {
      return ['code', 'dentist', 'status', 'nextApt', 'alerts']
    }
  })
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() => {
    try {
      const saved = localStorage.getItem('patients-view-mode')
      return saved === 'cards' ? 'cards' : 'table'
    } catch {
      return 'table'
    }
  })

  // Filter wrappers that reset page
  const setStatus = (val: string) => { setStatusFilter(val); setPage(1) }
  const setAlerts = (val: string) => { setAlertsFilter(val); setPage(1) }
  const setDentist = (val: string) => { setDentistFilter(val); setPage(1) }
  const setAge = (val: string) => { setAgeFilter(val); setPage(1) }

  const toggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      localStorage.setItem('patients-visible-columns', JSON.stringify(next))
      return next
    })
  }

  const toggleViewMode = () => {
    setViewMode((prev) => {
      const next = prev === 'table' ? 'cards' : 'table'
      localStorage.setItem('patients-view-mode', next)
      return next
    })
  }

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (statusFilter !== 'ALL') params.set('status', statusFilter)
    if (alertsFilter !== 'ALL') params.set('alerts', alertsFilter)
    if (dentistFilter !== 'ALL') params.set('dentistId', dentistFilter)
    if (ageFilter !== 'ALL') params.set('age', ageFilter)
    if (page > 1) params.set('page', String(page))
    setSearchParams(params, { replace: true })
  }, [search, statusFilter, alertsFilter, dentistFilter, ageFilter, page])

  useOpenOnQueryParam('new', useCallback(() => setShowCreate(true), []))


  const { data: response, isLoading, isError, error, refetch } = useQuery<PatientsResponse>({
    queryKey: ['patients', search, statusFilter, alertsFilter, dentistFilter, ageFilter, page],
    queryFn: async () => {
      const params: Record<string, string | number | undefined> = {
        page,
        limit: 10,
      }
      if (search) params.search = search
      if (statusFilter !== 'ALL') params.status = statusFilter
      if (dentistFilter !== 'ALL') params.dentistId = dentistFilter
      if (alertsFilter === 'allergies') {
        params.hasAllergies = 'true'
      } else if (alertsFilter === 'contraindications') {
        params.hasContraindications = 'true'
      }
      if (ageFilter !== 'ALL') {
        const [min, max] = ageFilter.split('-')
        if (min) params.ageMin = parseInt(min, 10)
        if (max && !max.endsWith('+')) params.ageMax = parseInt(max, 10)
        if (max?.endsWith('+')) params.ageMin = parseInt(min, 10)
      }
      const { data } = await api.get('/patients', { params })
      return data
    },
  })

  const patients = response?.data || []
  const totalPatients = response?.total || 0
  const totalPages = response?.totalPages || 1

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-bold text-text-primary">Pacientes</h1>
        <ErrorState message={error?.message} onRetry={refetch} />
      </div>
    )
  }

  const { data: dentists } = useQuery<User[]>({
    queryKey: ['dentists'],
    queryFn: async () => {
      const { data } = await api.get('/users')
      return (data as User[]).filter((u) => u.role === 'DENTIST' || u.role === 'HYGIENIST')
    },
  })

  const createPatient = useMutation({
    mutationFn: async (body: CreatePatientInput) => {
      const { data } = await api.post('/patients', body)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      setShowCreate(false)
      toast.success('Paciente creado correctamente')
    },
    onError: () => toast.error('Error al crear el paciente'),
  })

  const dentistMap = new Map(dentists?.map((d) => [d.id, `${d.firstName} ${d.lastName}`]))

  const allColumnDefs: Record<string, { header: string; sortable?: boolean; sortFn?: (a: Patient, b: Patient) => number; cell: (patient: Patient) => React.ReactNode }> = {
    code: {
      header: 'Codigo',
      sortable: true,
      sortFn: (a, b) => a.patientCode.localeCompare(b.patientCode),
      cell: (patient) => <span className="text-sm text-text-secondary tabular-nums">{patient.patientCode}</span>,
    },
    age: {
      header: 'Edad',
      sortable: true,
      sortFn: (a, b) => (getAge(a.birthDate) ?? -1) - (getAge(b.birthDate) ?? -1),
      cell: (patient) => {
        const age = getAge(patient.birthDate)
        return <span className="text-sm text-text-secondary">{age !== null ? `${age} anos` : '—'}</span>
      },
    },
    gender: {
      header: 'Genero',
      sortable: true,
      sortFn: (a, b) => (a.gender || '').localeCompare(b.gender || ''),
      cell: (patient) => {
        const map: Record<string, string> = { MALE: 'M', FEMALE: 'F', OTHER: 'Otro' }
        return <span className="text-sm text-text-secondary">{map[patient.gender ?? ''] || '—'}</span>
      },
    },
    dentist: {
      header: 'Dentista',
      sortable: true,
      sortFn: (a, b) =>
        (dentistMap.get(a.primaryDentistId ?? '') ?? '').localeCompare(
          dentistMap.get(b.primaryDentistId ?? '') ?? ''
        ),
      cell: (patient) => {
        const name = patient.primaryDentistId ? dentistMap.get(patient.primaryDentistId) : null
        return name ? (
          <span className="text-sm text-text-secondary flex items-center gap-1">
            <Stethoscope className="w-3.5 h-3.5 text-text-tertiary" />
            {name}
          </span>
        ) : (
          <span className="text-sm text-text-tertiary">—</span>
        )
      },
    },
    status: {
      header: 'Estado',
      sortable: true,
      sortFn: (a, b) => a.status.localeCompare(b.status),
      cell: (patient) => {
        const status = statusMap[patient.status] || { label: patient.status, variant: 'default' }
        return <Badge variant={status.variant} dot>{status.label}</Badge>
      },
    },
    nextApt: {
      header: 'Prox. cita',
      sortable: true,
      sortFn: (a, b) => {
        const da = a.appointments?.[0]?.startTime ?? ''
        const db = b.appointments?.[0]?.startTime ?? ''
        return da.localeCompare(db)
      },
      cell: (patient) => {
        const nextApt = patient.appointments?.[0]
        return (
          <span className="text-sm text-text-secondary">
            {nextApt ? new Date(nextApt.startTime).toLocaleDateString('es-ES') : '—'}
          </span>
        )
      },
    },
    lastVisit: {
      header: 'Ultima visita',
      sortable: true,
      sortFn: (a, b) => {
        const getLast = (p: Patient) =>
          p.appointments?.filter((ap) => ap.status === 'COMPLETED')
            .sort((x, y) => new Date(y.startTime).getTime() - new Date(x.startTime).getTime())[0]?.startTime ?? ''
        return getLast(a).localeCompare(getLast(b))
      },
      cell: (patient) => {
        const last = patient.appointments
          ?.filter((ap) => ap.status === 'COMPLETED')
          .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())[0]
        return (
          <span className="text-sm text-text-secondary">
            {last ? new Date(last.startTime).toLocaleDateString('es-ES') : '—'}
          </span>
        )
      },
    },
    alerts: {
      header: 'Alertas',
      cell: (patient) => (
        <div className="flex gap-1">
          {patient.hasAllergies && <Badge variant="error">Alergia</Badge>}
          {patient.hasContraindications && <Badge variant="warning">Contra.</Badge>}
        </div>
      ),
    },
  }

  const columns = [
    {
      header: 'Paciente',
      sortable: true,
      sortFn: (a: Patient, b: Patient) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
      cell: (patient: Patient) => {
        const initials = `${patient.firstName[0]}${patient.lastName[0]}`.toUpperCase()
        return (
          <div className="flex items-center gap-3">
            {patient.photoUrl ? (
              <img
                src={patient.photoUrl}
                alt={`${patient.firstName} ${patient.lastName}`}
                className="w-9 h-9 rounded-full object-cover border border-border flex-shrink-0"
                loading="lazy"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                {initials}
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-text-primary">
                {patient.firstName} {patient.lastName}
              </p>
            </div>
          </div>
        )
      },
    },
    {
      header: 'Contacto',
      cell: (patient: Patient) => (
        <div className="space-y-0.5">
          {patient.phone && (
            <div className="flex items-center gap-1.5 text-sm text-text-secondary">
              <Phone className="w-3.5 h-3.5 text-text-tertiary" />
              {patient.phone}
            </div>
          )}
          {patient.email && (
            <div className="flex items-center gap-1.5 text-sm text-text-secondary">
              <Mail className="w-3.5 h-3.5 text-text-tertiary" />
              {patient.email}
            </div>
          )}
        </div>
      ),
    },
    ...visibleColumns.map((key) => allColumnDefs[key]).filter(Boolean),
  ]

  // Stats
  const stats = {
    total: patients?.length ?? 0,
    active: patients?.filter((p) => p.status === 'ACTIVE').length ?? 0,
    newThisMonth: patients?.filter((p) => {
      if (!p.createdAt) return false
      const d = new Date(p.createdAt)
      const now = new Date()
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
    }).length ?? 0,
    withNextApt: patients?.filter((p) =>
      p.appointments?.some((a) => new Date(a.startTime) >= new Date())
    ).length ?? 0,
    inactive: patients?.filter((p) => {
      const past = p.appointments?.filter((a) => new Date(a.startTime) < new Date())
      if (!past?.length) return p.status === 'ACTIVE'
      const last = past.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())[0]
      const days = (Date.now() - new Date(last.startTime).getTime()) / (1000 * 60 * 60 * 24)
      return days > 180
    }).length ?? 0,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Pacientes</h1>
          <p className="text-sm text-text-tertiary mt-0.5">Gestiona las fichas de tus pacientes</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4" />
          Nuevo paciente
        </Button>
      </div>

      {/* Stats bar */}
      {patients && patients.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            {
              label: 'Total',
              value: stats.total,
              icon: Users,
              color: 'text-text-primary',
              bg: 'bg-surface-1 border-border',
              onClick: () => setStatus('ALL'),
            },
            {
              label: 'Activos',
              value: stats.active,
              icon: UserCheck,
              color: 'text-success-600',
              bg: 'bg-success-50 border-success-200',
              onClick: () => setStatus('ACTIVE'),
            },
            {
              label: 'Nuevos este mes',
              value: stats.newThisMonth,
              icon: UserPlus,
              color: 'text-primary-600',
              bg: 'bg-primary-50 border-primary-200',
              onClick: () => {},
            },
            {
              label: 'Con cita prox.',
              value: stats.withNextApt,
              icon: CalendarCheck,
              color: 'text-info-600',
              bg: 'bg-info-50 border-info-200',
              onClick: () => {},
            },
            {
              label: 'Inactivos >6m',
              value: stats.inactive,
              icon: UserX,
              color: 'text-warning-600',
              bg: 'bg-warning-50 border-warning-200',
              onClick: () => setStatus('INACTIVE'),
            },
          ].map((s) => (
            <button
              key={s.label}
              onClick={s.onClick}
              className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors hover:shadow-sm ${s.bg}`}
            >
              <s.icon className={`w-5 h-5 ${s.color} flex-shrink-0`} />
              <div>
                <p className={`text-lg font-bold tabular-nums leading-none ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-text-tertiary mt-0.5 uppercase tracking-wider">{s.label}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreatePatientModal
          onClose={() => setShowCreate(false)}
          onSubmit={(data) => createPatient.mutate(data)}
          isPending={createPatient.isPending}
          dentists={dentists ?? []}
        />
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { key: 'ALL', label: 'Todos' },
          { key: 'ACTIVE', label: 'Activos' },
          { key: 'INACTIVE', label: 'Inactivos' },
          { key: 'ARCHIVED', label: 'Archivados' },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === f.key
                ? 'bg-primary-100 text-primary-700'
                : 'bg-surface-1 border border-border text-text-secondary hover:bg-surface-2'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="w-px h-5 bg-border mx-1" />
        {[
          { key: 'ALL', label: 'Todas las alertas' },
          { key: 'allergies', label: 'Con alergia' },
          { key: 'contraindications', label: 'Con contraindicacion' },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setAlerts(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              alertsFilter === f.key
                ? 'bg-primary-100 text-primary-700'
                : 'bg-surface-1 border border-border text-text-secondary hover:bg-surface-2'
            }`}
          >
            {f.label}
          </button>
        ))}
        {dentists && dentists.length > 0 && (
          <>
            <div className="w-px h-5 bg-border mx-1" />
            <select
              value={dentistFilter}
              onChange={(e) => setDentist(e.target.value)}
              className="bg-surface-1 border border-border rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary outline-none focus:border-primary-400"
            >
              <option value="ALL">Todos los dentistas</option>
              {dentists.map((d) => (
                <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>
              ))}
            </select>
          </>
        )}
        <div className="w-px h-5 bg-border mx-1" />
        <div className="flex items-center gap-1">
          <Baby className="w-3.5 h-3.5 text-text-tertiary" />
          <select
            value={ageFilter}
            onChange={(e) => setAge(e.target.value)}
            className="bg-surface-1 border border-border rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary outline-none focus:border-primary-400"
          >
            <option value="ALL">Todas las edades</option>
            <option value="0-17">0-17 años</option>
            <option value="18-30">18-30 años</option>
            <option value="31-50">31-50 años</option>
            <option value="51-65">51-65 años</option>
            <option value="65+">65+ años</option>
          </select>
        </div>
        {/* View mode toggle */}
        <button
          onClick={toggleViewMode}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-1 border border-border text-xs font-medium text-text-secondary hover:bg-surface-2 transition-colors"
          title={viewMode === 'table' ? 'Ver como tarjetas' : 'Ver como tabla'}
        >
          {viewMode === 'table' ? (
            <>
              <LayoutGrid className="w-3.5 h-3.5" />
              Tarjetas
            </>
          ) : (
            <>
              <Table className="w-3.5 h-3.5" />
              Tabla
            </>
          )}
        </button>

        {/* Column selector */}
        <div className="relative">
          <button
            onClick={() => setShowColumnMenu(!showColumnMenu)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-1 border border-border text-xs font-medium text-text-secondary hover:bg-surface-2 transition-colors"
          >
            <LayoutList className="w-3.5 h-3.5" />
            Columnas
          </button>
          {showColumnMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowColumnMenu(false)} />
              <div className="absolute z-20 mt-1 right-0 bg-surface-1 border border-border rounded-lg shadow-lg p-2 min-w-[180px]">
                {[
                  { key: 'code', label: 'Codigo' },
                  { key: 'age', label: 'Edad' },
                  { key: 'gender', label: 'Genero' },
                  { key: 'dentist', label: 'Dentista' },
                  { key: 'status', label: 'Estado' },
                  { key: 'nextApt', label: 'Prox. cita' },
                  { key: 'lastVisit', label: 'Ultima visita' },
                  { key: 'alerts', label: 'Alertas' },
                ].map((col) => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-surface-2 cursor-pointer text-sm"
                  >
                    {visibleColumns.includes(col.key) ? (
                      <Eye className="w-3.5 h-3.5 text-primary-600" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5 text-text-tertiary" />
                    )}
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(col.key)}
                      onChange={() => toggleColumn(col.key)}
                      className="sr-only"
                    />
                    <span className={visibleColumns.includes(col.key) ? 'text-text-primary' : 'text-text-tertiary'}>
                      {col.label}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {selectedIds.length > 0 && (
          <>
            <div className="w-px h-5 bg-border mx-1" />
            <span className="text-xs text-text-secondary">{selectedIds.length} seleccionados</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                const selected = patients?.filter((p) => selectedIds.includes(p.id)) ?? []
                const headers = ['Codigo', 'Nombre', 'Apellidos', 'Telefono', 'Email', 'Estado']
                const rows = selected.map((p) => [
                  p.patientCode,
                  p.firstName,
                  p.lastName,
                  p.phone ?? '',
                  p.email ?? '',
                  p.status,
                ])
                const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n')
                const blob = new Blob([csv], { type: 'text/csv' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'pacientes-seleccionados.csv'
                a.click()
                URL.revokeObjectURL(url)
              }}
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Exportar seleccion
            </Button>
          </>
        )}
      </div>

      {viewMode === 'table' ? (
        <DataTable
          columns={columns}
          data={patients}
          keyExtractor={(p) => p.id}
          isLoading={isLoading}
          searchable
          searchPlaceholder="Buscar por nombre, telefono, DNI..."
          searchValue={search}
          onSearchChange={(val) => { setSearch(val); setPage(1) }}
          exportable
          exportFileName="pacientes.csv"
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          emptyState={{
            icon: <UserPlus className="w-10 h-10 text-text-tertiary mb-3" />,
            title: 'No se encontraron pacientes',
            description: 'Prueba ajustando la busqueda o crea tu primer paciente.',
            action: (
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4" />
                Crear primer paciente
              </Button>
            ),
            tutorialSlug: 'crear-paciente',
            tutorialTitle: 'Crear un nuevo paciente',
          }}
          actions={(patient) => (
            <div className="flex items-center gap-0.5">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/appointments?patientId=${patient.id}`)
                }}
                className="p-1.5 rounded-lg hover:bg-surface-1 text-text-tertiary hover:text-primary-600 transition-colors"
                title="Crear cita"
              >
                <CalendarPlus className="w-3.5 h-3.5" />
              </button>
              {patient.phone && (
                <a
                  href={`tel:${patient.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded-lg hover:bg-surface-1 text-text-tertiary hover:text-primary-600 transition-colors"
                  title="Llamar"
                >
                  <Phone className="w-3.5 h-3.5" />
                </a>
              )}
              {patient.email && (
                <a
                  href={`mailto:${patient.email}`}
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded-lg hover:bg-surface-1 text-text-tertiary hover:text-indigo-600 transition-colors"
                  title="Email"
                >
                  <Mail className="w-3.5 h-3.5" />
                </a>
              )}
              <Link
                to={`/patients/${patient.id}`}
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 rounded-lg hover:bg-surface-3 text-text-tertiary hover:text-text-secondary transition-colors inline-flex"
              >
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        />
      ) : (
        /* Cards View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-surface-1 border border-border rounded-xl p-4 space-y-3 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-surface-2" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 bg-surface-2 rounded w-3/4" />
                    <div className="h-3 bg-surface-2 rounded w-1/2" />
                  </div>
                </div>
                <div className="h-3 bg-surface-2 rounded w-full" />
                <div className="h-3 bg-surface-2 rounded w-2/3" />
              </div>
            ))
          ) : patients.length === 0 ? (
            <div className="col-span-full text-center py-10">
              <UserPlus className="w-10 h-10 text-text-tertiary mx-auto mb-3" />
              <p className="text-sm text-text-tertiary">No se encontraron pacientes</p>
              <Button onClick={() => setShowCreate(true)} className="mt-2">
                <Plus className="w-4 h-4 mr-1" />
                Crear primer paciente
              </Button>
            </div>
          ) : (
            patients.map((patient) => {
              const initials = `${patient.firstName[0]}${patient.lastName[0]}`.toUpperCase()
              const nextApt = patient.appointments?.[0]
              const status = statusMap[patient.status] || { label: patient.status, variant: 'default' }
              return (
                <Link
                  key={patient.id}
                  to={`/patients/${patient.id}`}
                  className="bg-surface-1 border border-border rounded-xl p-4 hover:border-primary-300 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start gap-3">
                    {patient.photoUrl ? (
                      <img
                        src={patient.photoUrl}
                        alt={`${patient.firstName} ${patient.lastName}`}
                        className="w-12 h-12 rounded-full object-cover border border-border flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {initials}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-xs text-text-tertiary">{patient.patientCode}</p>
                      <Badge variant={status.variant} className="text-[10px] mt-1">
                        {status.label}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    {patient.phone && (
                      <p className="text-xs text-text-secondary flex items-center gap-1">
                        <Phone className="w-3 h-3 text-text-tertiary" />
                        {patient.phone}
                      </p>
                    )}
                    {nextApt && (
                      <p className="text-xs text-primary-600 flex items-center gap-1">
                        <CalendarPlus className="w-3 h-3" />
                        {new Date(nextApt.startTime).toLocaleDateString('es-ES')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
                    {patient.phone && (
                      <a
                        href={`tel:${patient.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 rounded-lg hover:bg-surface-2 text-text-tertiary hover:text-primary-600 transition-colors"
                        title="Llamar"
                      >
                        <Phone className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {patient.email && (
                      <a
                        href={`mailto:${patient.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 rounded-lg hover:bg-surface-2 text-text-tertiary hover:text-indigo-600 transition-colors"
                        title="Email"
                      >
                        <Mail className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/appointments?patientId=${patient.id}`)
                      }}
                      className="p-1.5 rounded-lg hover:bg-surface-2 text-text-tertiary hover:text-primary-600 transition-colors ml-auto"
                      title="Crear cita"
                    >
                      <CalendarPlus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </Link>
              )
            })
          )}
        </div>
      )}

      {/* Server-side pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg bg-surface-1 border border-border text-xs text-text-secondary hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Anterior
          </button>
          <span className="text-xs text-text-secondary">
            Pagina {page} de {totalPages} · {totalPatients} pacientes
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 rounded-lg bg-surface-1 border border-border text-xs text-text-secondary hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  )
}
